import React, { useEffect, useState } from "react";
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Users,
  Building,
  UserPlus,
  Settings,
  Eye,
  EyeOff,
  Calendar,
  Mail,
  Phone,
  MapPin,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  Entity,
  User,
  ErrorResponse,
  RoleEntity,
  EmptyResponse,
  UserRole,
} from "../types";
import {
  getEntities,
  createEntity,
  updateEntity,
  deleteEntity,
} from "../services/entities";
import { createUser, updateUser, deleteUser } from "../services/users";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getUsers } from "../services/users";
import { getRoles } from "../services/roles";
import Notification, { NotificationAction } from "./Notification";
import { toast } from "react-toastify";
import Header from "./Header";
import { useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom";

interface EntityFormData
  extends Omit<Entity, "id" | "createdAt" | "userCount" | "status"> {
  status: string;
}

interface UserFormData
  extends Omit<
    User,
    "id" | "createdAt" | "lastAccessAt" | "organization" | "status"
  > {
  password: string;
  status: string;
  confirmPassword: string;
  roleId: string | undefined;
}

type FormData =
  | ({ type: "entity" } & EntityFormData)
  | ({ type: "user" } & UserFormData);

export default function Entities() {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const autoOpenCreateForm = searchParams.get("create") === "true";
  const createType = searchParams.get("type") as "entity" | "user" | null;
  const { translations, token, role: roleUser } = useAuth();
  
  const itemId = params.id;
  const isEditMode = location.pathname.endsWith("/edit");
  const isViewMode = !!itemId && !isEditMode;
  
  const [activeTab, setActiveTab] = useState<"entities" | "users">("entities");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [notification, setNotification] = useState<{
    type: "success" | "error" | "warning" | "info";
    title: string;
    duration?: number;
    message?: string;
    actions?: NotificationAction[];
  } | null>(null);
  const {
    data: entitiesRaw,
    isLoading,
    error,
  } = useQuery<Entity[]>({
    queryKey: ["entities"],
    queryFn: () => getEntities(token!),
    enabled: !!token,
  });
  const {
    data: usersRaw,
    isLoading: isLoadingUsers,
    error: errorUsers,
  } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => getUsers(token!),
    enabled: !!token,
  });
  const {
    data: rolesRaw,
    isLoading: isLoadingRoles,
    error: errorRoles,
  } = useQuery({
    queryKey: ["roles"],
    queryFn: () => getRoles(token!),
    enabled: !!token,
  });
  const queryClient = useQueryClient();
  const entities = entitiesRaw ?? [];
  const users = usersRaw ?? [];

  // Determine if editing entity or user, and find the item
  const editingItem = itemId 
    ? (entities.find(e => e.id === itemId) || users.find(u => u.id === itemId) || null)
    : null;
  
  // Determine if editing item is a user or entity
  const editingItemType: "entity" | "user" | null = editingItem
    ? ("fullName" in editingItem && "role" in editingItem ? "user" : "entity")
    : null;
  
  // Determine active tab based on editing item type or create type
  useEffect(() => {
    if (editingItemType) {
      setActiveTab(editingItemType === "user" ? "users" : "entities");
    } else if (autoOpenCreateForm && createType) {
      setActiveTab(createType === "user" ? "users" : "entities");
    }
  }, [editingItemType, autoOpenCreateForm, createType]);

  const showCreateForm = autoOpenCreateForm || !!itemId;
  const editOrShowMode = isEditMode ? "edit" : (isViewMode ? "show" : "edit");

  useEffect(() => {
    // If we navigate to an item detail but it doesn't exist, redirect to list
    if (itemId && !isLoading && !isLoadingUsers && entities.length > 0 && users.length > 0 && !editingItem) {
      navigate("/entities", { replace: true });
    }
  }, [itemId, isLoading, isLoadingUsers, entities, users, editingItem, navigate]);

  // Mock entities data
  const mockEntities: Entity[] = [
    {
      id: "1",
      name: "Fundación Esperanza",
      description: "Organización dedicada al desarrollo educativo y social",
      email: "contacto@fundacionesperanza.org",
      phone: "+34 123 456 789",
      address: "Calle Mayor 123, Barcelona",
      status: 1,
      createdAt: "2024-01-10T10:00:00Z",
      userCount: 3,
    },
    {
      id: "2",
      name: "ONG Cambio Social",
      description: "Trabajamos por el cambio social y la inclusión",
      email: "info@cambiosocial.org",
      phone: "+34 987 654 321",
      address: "Avenida Libertad 456, Madrid",
      status: 1,
      createdAt: "2024-01-08T14:30:00Z",
      userCount: 2,
    },
    {
      id: "3",
      name: "Asociación Futuro Verde",
      description: "Proyectos ambientales y sostenibilidad",
      email: "contacto@futuroverde.org",
      phone: "+34 555 123 456",
      address: "Plaza Verde 789, Valencia",
      status: 0,
      createdAt: "2024-01-05T09:15:00Z",
      userCount: 1,
    },
  ];

  // Mock users data
  const mockUsers: User[] = [
    {
      id: "1",
      fullName: "Juan Pérez",
      email: "admin@redmimbre.org",
      role: "Administrator",
      status: 1,
      createdAt: "2024-01-01T00:00:00Z",
      lastAccessAt: "2024-01-15T10:30:00Z",
    },
    {
      id: "2",
      fullName: "María González",
      email: "maria@fundacionesperanza.org",
      role: "Organization",
      organizationId: "1",
      organization: "Fundación Esperanza",
      status: 1,
      createdAt: "2024-01-10T10:00:00Z",
      lastAccessAt: "2024-01-14T16:45:00Z",
    },
    {
      id: "3",
      fullName: "Carlos Ruiz",
      email: "carlos@cambiosocial.org",
      role: "Organization",
      organizationId: "2",
      organization: "ONG Cambio Social",
      status: 1,
      createdAt: "2024-01-08T14:30:00Z",
      lastAccessAt: "2024-01-13T09:20:00Z",
    },
    {
      id: "4",
      fullName: "Ana Martín",
      email: "ana@futuroverde.org",
      role: "Organization",
      organizationId: "3",
      organization: "Asociación Futuro Verde",
      status: 0,
      createdAt: "2024-01-05T09:15:00Z",
      lastAccessAt: "2024-01-10T14:00:00Z",
    },
  ];

  const filteredEntities = entities.filter((entity) => {
    const matchesSearch =
      entity?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entity?.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && entity.status === 1) ||
      (statusFilter === "inactive" && entity.status === 0);

    return matchesSearch && matchesStatus;
  });

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user?.organization?.toLowerCase().includes(searchTerm.toLowerCase()) ??
        false);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && user.status === 1) ||
      (statusFilter === "inactive" && user.status === 0);

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: number) => {
    const statusConfig = {
      1: { color: "green", label: "Activa" },
      0: { color: "red", label: "Inactiva" },
      2: { color: "yellow", label: "Pendiente" },
    };
    const statusKey = status as 0 | 1 | 2;
    const config = statusConfig[statusKey] || statusConfig[2];

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}
      >
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      Administrator: { color: "purple", label: "Administrador" },
      Organization: { color: "blue", label: "Organización" },
    };
    const config =
      roleConfig[role as keyof typeof roleConfig] || roleConfig.Organization;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}
      >
        {config.label}
      </span>
    );
  };

  const handleDeleteEntity = async (type: string, entity: Entity | User) => {
    let response: ErrorResponse | { ok: boolean } = { ok: false };
    if (type === "entity") {
      response = await deleteEntity(entity as Entity, token!);
    } else if (type === "user") {
      response = await deleteUser(entity as User, token!);
    }

    if ("error" in response) {
      console.error("Error deleting entity:", response.message);
      setNotification({
        type: "error",
        title: `Error al eliminar ${type}`,
        message: response.message,
      });
      return response;
    }

    // Refrescar lista
    queryClient.invalidateQueries({ queryKey: ["entities"] });
    queryClient.invalidateQueries({ queryKey: ["users"] });
    // Mostrar éxito
    setNotification({
      type: "success",
      title: `Eliminación de ${type} exitosa`,
    });
    return response;
  };
  const handleNavigateToEntities = () => {
    navigate(`/entities?create=true&type=entity`);
  };
  // Notificación inline global SIEMPRE visible, no solo en showCreateForm
  const notificationComponent = notification && (
    <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
      <Notification
        type={notification.type}
        title={notification.title}
        actions={notification.actions}
        message={notification.message}
        duration={notification.actions?.length ? 0 : 5000}
        onClose={() => setNotification(null)}
      />
    </div>
  );

  // Mostrar estado de carga
  if (isLoading) {
    return (
      <>
        {notificationComponent}
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {activeTab === "entities"
                  ? translations.entities
                  : translations.users}
              </h1>
              <p className="text-gray-600 mt-1">
                {activeTab === "entities"
                  ? translations.loadingEntities
                  : translations.loadingUsers}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">
                {activeTab === "entities"
                  ? translations.loadingEntities
                  : translations.loadingUsers}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (showCreateForm) {
    return (
      <>
        {notificationComponent}
        <EntityUserForm
          type={editingItemType || createType || (activeTab === "entities" ? "entity" : "user")}
          mode={editOrShowMode}
          item={editingItem}
          entities={entities}
          onClose={() => navigate("/entities")}
          roles={rolesRaw || []}
          userRole={roleUser}
          setNotification={setNotification}
          loadingEntities={isLoading}
          onNavigateToEntities={handleNavigateToEntities}
        />
      </>
    );
  }

  return (
    <>
      {notificationComponent}
      <div className="p-6 space-y-6">
        {/* Header */}
        <Header>
          <div className="flex items-center justify-between w-full">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {translations.manageEntities}
              </h1>
              <p className="text-gray-600 mt-1">
                {translations.manageEntitiesDescription}
              </p>
            </div>
          </div>
        </Header>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("entities")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "entities"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center">
                  <Building className="w-4 h-4 mr-2" />
                  {translations.entities} ({entities.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab("users")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "users"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  {translations.users} ({users.length})
                </div>
              </button>
            </nav>
          </div>

          {/* Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder={`${translations.search} ${
                      activeTab === "entities"
                        ? translations.entities
                        : translations.users
                    }...`}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(
                      e.target.value as "all" | "active" | "inactive"
                    )
                  }
                >
                  <option value="all">{translations.allStates}</option>
                  <option value="active">{translations.active}</option>
                  <option value="inactive">{translations.inactive}</option>
                </select>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => navigate(`/entities?create=true&type=${activeTab === "entities" ? "entity" : "user"}`)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {translations.add || "Añadir"}
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-x-auto">
            {activeTab === "entities" ? (
              <EntitiesTable
                entities={filteredEntities}
                onEdit={(entity, edit = true) => {
                  const path = edit ? `/entities/${entity.id}/edit` : `/entities/${entity.id}`;
                  navigate(path);
                }}
                getStatusBadge={getStatusBadge}
                onDelete={async (entity) => {
                  setNotification({
                    type: "warning",
                    title: translations.confirmDeleteEntity,
                    message: translations.actionCannotBeUndone,
                    actions: [
                      {
                        label: translations.yesDelete,
                        onClick: async (close) => {
                          const response = await handleDeleteEntity(
                            activeTab === "entities" ? "entity" : "user",
                            entity
                          );
                          if (!("error" in response) && response?.ok) {
                            close();
                          }
                        },
                        className: "bg-red-600 text-white",
                      },
                      {
                        label: translations.cancel,
                        onClick: (close) => close(),
                        className: "bg-gray-300 text-black",
                      },
                    ],
                  });
                }}
                setNotification={setNotification}
                translations={translations}
              />
            ) : (
              <UsersTable
                users={filteredUsers}
                onEdit={(user, edit = true) => {
                  const path = edit ? `/entities/${user.id}/edit` : `/entities/${user.id}`;
                  navigate(path);
                }}
                getStatusBadge={getStatusBadge}
                getRoleBadge={getRoleBadge}
                onDelete={(user) => {
                  setNotification({
                    type: "warning",
                    title: translations.confirmDeleteUser,
                    message: translations.actionCannotBeUndone,
                    duration: 5000,
                    actions: [
                      {
                        label: translations.yesDelete,
                        onClick: async (close) => {
                          const response = await handleDeleteEntity(
                            "user",
                            user
                          );
                          if (!("error" in response) && response?.ok) {
                            close();
                          }
                        },
                        className: "bg-red-600 text-white",
                      },
                      {
                        label: translations.cancel,
                        onClick: (close) => close(),
                        className: "bg-gray-300 text-black",
                      },
                    ],
                  });
                }}
                setNotification={setNotification}
                entities={entities}
                translations={translations}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

interface EntitiesTableProps {
  entities: Entity[];
  onEdit: (entity: Entity, edit?: boolean) => void;
  getStatusBadge: (status: number) => JSX.Element;
  onDelete: (entity: Entity) => void;
  translations: Record<string, string>;
}

function EntitiesTable({
  entities,
  onEdit,
  getStatusBadge,
  onDelete,
  setNotification,
  translations,
}: EntitiesTableProps & { setNotification: any }) {
  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.entity}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.contactInfo}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.users}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.status}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.date}
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.actions}
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {entities.length === 0 ? (
          <tr>
            <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
              {translations.noEntities}
            </td>
          </tr>
        ) : (
          entities.map((entity) => (
            <tr key={entity.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {entity.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {entity.description}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  <div className="flex items-center mb-1">
                    <Mail className="w-3 h-3 mr-1 text-gray-400" />
                    {entity.contactEmail}
                  </div>
                  {entity.phone && (
                    <div className="flex items-center mb-1">
                      <Phone className="w-3 h-3 mr-1 text-gray-400" />
                      {entity.phone}
                    </div>
                  )}
                  {entity.address && (
                    <div className="flex items-center">
                      <MapPin className="w-3 h-3 mr-1 text-gray-400" />
                      <span className="text-xs">{entity.address}</span>
                    </div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">
                    {entity.userCount}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getStatusBadge(entity.status)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(entity.createdAt).toLocaleDateString()}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => onEdit(entity, false)}
                    className="text-blue-600 hover:text-blue-900 p-1 rounded"
                    title={translations.viewDetails}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onEdit(entity)}
                    className="text-green-600 hover:text-green-900 p-1 rounded"
                    title={translations.editEntity}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(entity)}
                    className="text-red-600 hover:text-red-900 p-1 rounded"
                    title={translations.deleteEntity}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

interface UsersTableProps {
  users: User[];
  onEdit: (user: User, edit?: boolean) => void;
  getStatusBadge: (status: number) => JSX.Element;
  getRoleBadge: (role: string) => JSX.Element;
  onDelete: (user: User) => void;
  translations: Record<string, string>;
}

function UsersTable({
  users,
  onEdit,
  getStatusBadge,
  getRoleBadge,
  onDelete,
  entities,
  setNotification,
  translations,
}: UsersTableProps & { entities: Entity[] } & { setNotification: any }) {
  // Buscar el nombre de la organización a partir del organizationId
  const getOrganizationName = (user: User) => {
    if (!user.organization) return "-";
    const entity = entities.find((e) => e.id === user.organizationId);
    if (entity) return entity.name;
    return user.organization || "-";
  };

  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.user}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.role}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.organization}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.status}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.lastAccess}
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            {translations.actions}
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {users.length === 0 ? (
          <tr>
            <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
              {translations.noUsers}
            </td>
          </tr>
        ) : (
          users.map((user) => (
            <tr key={user.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {user.fullName}
                  </div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getRoleBadge(user.role)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {getOrganizationName(user)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getStatusBadge(user.status)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {user.lastAccessAt ? (
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(user.lastAccessAt).toLocaleDateString()}
                  </div>
                ) : (
                  translations.never
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => onEdit(user, false)}
                    className="text-blue-600 hover:text-blue-900 p-1 rounded"
                    title={translations.viewProfile}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onEdit(user)}
                    className="text-green-600 hover:text-green-900 p-1 rounded"
                    title={translations.editUser}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(user)}
                    className="text-red-600 hover:text-red-900 p-1 rounded"
                    title={translations.deleteUser}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
function isFormData<T extends "entity" | "user">(
  data: FormData,
  type: T
): data is Extract<FormData, { type: T }> {
  return data.type === type;
}
interface EntityUserFormProps {
  type: "entity" | "user";
  mode?: "edit" | "show";
  item: Entity | User | null;
  entities: Entity[];
  onClose: () => void;
  roles: RoleEntity[];
  userRole: string | null;
  loadingEntities: boolean;
  onNavigateToEntities?: () => void;
}

function EntityUserForm({
  type,
  mode = "edit",
  item,
  entities,
  onClose,
  roles,
  userRole,
  setNotification,
  loadingEntities,
  onNavigateToEntities,
}: EntityUserFormProps & { setNotification: any }) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const queryClient = useQueryClient();
  const { translations, token } = useAuth();
  const isEditing = !!item && mode === "edit";
  const isViewingOrEditing = !!item;

  // Función para validar si el formulario es válido
  const isFormValid = (): boolean => {
    if (type === "entity" && isFormData(formData, "entity")) {
      return !!(
        formData.name?.trim() &&
        formData.email?.trim() &&
        formData.description?.trim() &&
        formData.phone?.trim() &&
        formData.address?.trim() &&
        !formErrors.name &&
        !formErrors.email &&
        !formErrors.description &&
        !formErrors.phone &&
        !formErrors.address
      );
    }
    if (type === "user" && isFormData(formData, "user")) {
      const hasPassword = !!formData.password && formData.password.trim().length > 0;
      const meetsLength = hasPassword ? formData.password.length >= 8 : !isEditing;
      const hasUpper = hasPassword ? /[A-Z]/.test(formData.password) : !isEditing;
      const hasLower = hasPassword ? /[a-z]/.test(formData.password) : !isEditing;
      const hasNumber = hasPassword ? /\d/.test(formData.password) : !isEditing;
      const passwordValid = isEditing 
        ? (!hasPassword || (meetsLength && hasUpper && hasLower && hasNumber))
        : (meetsLength && hasUpper && hasLower && hasNumber);
      const passwordsMatch = isEditing
        ? (!hasPassword || formData.password === formData.confirmPassword)
        : formData.password === formData.confirmPassword;
      
      const basicFieldsValid = !!(
        formData.fullName?.trim() &&
        formData.email?.trim() &&
        !formErrors.fullName &&
        !formErrors.email
      );
      
      const organizationValid = formData.role === "Organization"
        ? !!formData.organizationId
        : true;
      
      return basicFieldsValid && passwordValid && passwordsMatch && organizationValid &&
        !formErrors.password &&
        !formErrors.confirmPassword &&
        !formErrors.organizationId;
    }
    return false;
  };
  const roleInitial =
    roles.find((role: any) => role.name === "Organization")?.name ||
    "Organization";
  const roleInitialId =
    roles.find((role: any) => role.name === "Organization")?.id || "";
  const [formData, setFormData] = useState<FormData>(() => {
    if (type === "entity") {
      const entity = item as Entity | undefined;
      return {
        type: "entity",
        name: entity?.name || "",
        description: entity?.description || "",
        email: entity?.contactEmail || "",
        phone: entity?.phone || "",
        address: entity?.address || "",
        status: entity?.status === 1 ? "active" : "inactive",
      };
    } else {
      const user = item as User | undefined;
      return {
        type: "user",
        fullName: user?.fullName || "",
        email: user?.email || "",
        roleId:
          roles.find((role) => role.name === user?.role)?.id || roleInitialId,
        role: user?.role || roleInitial,
        organizationId:
          entities.find((entity) => entity.name === user?.organization)?.id ||
          "",
        status: user?.status === 1 ? "active" : "inactive",
        password: "",
        confirmPassword: "",
      };
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    const errors: { [key: string]: string } = {};
    if (type === "entity" && isFormData(formData, "entity")) {
      if (!formData.name) errors.name = translations.nameRequired;
      if (!formData.email) errors.email = translations.emailRequired;
      if (!formData.description)
        errors.description = translations.descriptionRequired;
      if (!formData.phone) errors.phone = translations.phoneRequired;
      if (!formData.address) errors.address = translations.addressRequired;
    }
    if (type === "user" && isFormData(formData, "user")) {
      if (!formData.fullName) errors.fullName = translations.fullNameRequired;
      if (!formData.email) errors.email = translations.emailRequired;
      if (formData.role === "Organization" && !formData.organizationId) {
        errors.organizationId = translations.organizationIdRequired;
      }
      const hasPassword = !!formData.password && formData.password.trim().length > 0;
      const meetsLength = hasPassword ? formData.password.length >= 8 : !isEditing;
      const hasUpper = hasPassword ? /[A-Z]/.test(formData.password) : !isEditing;
      const hasLower = hasPassword ? /[a-z]/.test(formData.password) : !isEditing;
      const hasNumber = hasPassword ? /\d/.test(formData.password) : !isEditing;
      if (!isEditing) {
        if (!formData.password) errors.password = translations.passwordRequired;
        if (!formData.confirmPassword)
          errors.confirmPassword = translations.confirmPasswordRequired;
        if (formData.password && !(meetsLength && hasUpper && hasLower && hasNumber)) {
          errors.password = translations.passwordRequirements as string;
        }
        if (formData.password !== formData.confirmPassword) {
          errors.confirmPassword = translations.passwordsDoNotMatch;
        }
      } else if (hasPassword) {
        if (!(meetsLength && hasUpper && hasLower && hasNumber)) {
          errors.password = translations.passwordRequirements as string;
        }
        if (!formData.confirmPassword) {
          errors.confirmPassword = translations.confirmPasswordRequired;
        } else if (formData.password !== formData.confirmPassword) {
          errors.confirmPassword = translations.passwordsDoNotMatch;
        }
      }
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setIsSaving(false);
      return;
    }
    let response: ErrorResponse | Entity | User | EmptyResponse;
    if (type === "entity") {
      const entityData = {
        ...(formData as Omit<
          Entity,
          "id" | "createdAt" | "userCount" | "status"
        >),
        status: formData.status === "active" ? 1 : 0,
        id: isEditing ? (item as Entity).id : "",
      };
      response = isEditing
        ? await updateEntity((item as Entity).id, entityData, token!)
        : await createEntity(entityData, token!);
    } else {
      type UpdateUserData = Omit<
        User,
        "id" | "createdAt" | "lastAccessAt" | "organization" | "status"
      > & { status: number; id: string; password?: string };
      const userData: UpdateUserData = {
        ...(formData as Omit<
          User,
          "id" | "createdAt" | "lastAccessAt" | "organization" | "status"
        >),
        status: formData.status === "active" ? 1 : 0,
        id: isEditing ? (item as User).id : "",
      };
      // En edición, enviar password solo si el usuario ha indicado una nueva
      if (
        isEditing &&
        isFormData(formData, "user") &&
        formData.password &&
        formData.password.trim().length > 0
      ) {
        userData.password = formData.password;
      }
      response = isEditing
        ? await updateUser((item as User).id, userData, token!)
        : await createUser(userData, token!);
    }

    if ("error" in response) {
      console.error("Error creating/updating entity:", response.message);
      setNotification({
        type: "error",
        title: `${translations.errorCreatingUpdating} ${
          isEditing ? translations.updating : translations.creating
        } ${type === "entity" ? translations.entity : translations.user}`,
        message: response.message,
      });
      setIsSaving(false);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["entities"] });
    queryClient.invalidateQueries({ queryKey: ["users"] });
    setNotification({
      type: "success",
      title: `${
        isEditing ? translations.updateAlone : translations.creatingAlone
      } de ${
        type === "entity" ? translations.entity : translations.user
      } exitosa`,
      message: undefined,
    });
    setIsSaving(false);
    onClose();
  };
  return (
    <div className="p-6 space-y-6">
      {isSaving && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl max-w-sm w-full mx-4">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-lg font-medium text-gray-900">
                {translations.savingUser || translations.saving}
              </p>
              <p className="text-sm text-gray-500 text-center">
                {translations.processing || translations.saving}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <Header>
        <div className="flex items-center gap-4">
          {isViewingOrEditing && (
            <button
              onClick={() => navigate("/entities")}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-gray-700"
              title={translations.back || "Volver"}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === "show"
                ? `${translations.view || "Ver"} ${type === "entity" ? translations.entity : translations.user}`
                : isEditing
                ? `${translations.updateAlone} ${type === "entity" ? translations.entity : translations.user}`
                : `${translations.creatingAlone} ${type === "entity" ? translations.entity : translations.user}`}
            </h1>
            <p className="text-gray-600 mt-1">
              {mode === "show"
                ? translations.viewDetails || "Detalles"
                : isEditing
                ? translations.modifyingData
                : translations.completingInformation}{" "}
              {type === "entity"
                ? translations.ofThe + " " + translations.organization
              : translations.ofThe + " " + translations.user}
          </p>
          </div>
        </div>
        {/* Botón cancelar eliminado para evitar redundancia */}
      </Header>
      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {type === "entity" && isFormData(formData, "entity") ? (
            <>
              {/* Entity Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.entityName} *
                  </label>
                  <input
                    type="text"
                    disabled={mode === "show"}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.name ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (formErrors.name) {
                        setFormErrors((prev) => ({ ...prev, name: "" }));
                      }
                    }}
                    placeholder="Ej: Fundación Esperanza"
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.contactEmail} *
                  </label>
                  <input
                    type="email"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.email ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (formErrors.email) {
                        setFormErrors((prev) => ({ ...prev, email: "" }));
                      }
                    }}
                    placeholder="contacto@entidad.org"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.email}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.description}
                </label>
                <textarea
                  rows={3}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent  ${
                    formErrors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  value={formData.description}
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    if (formErrors.description) {
                      setFormErrors((prev) => ({ ...prev, description: "" }));
                    }
                  }}
                  placeholder={translations.describeEntityMission}
                />
                {formErrors.description && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.phone}
                  </label>
                  <input
                    type="tel"
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent  ${
                      formErrors.phone ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData({ ...formData, phone: e.target.value });
                      if (formErrors.phone) {
                        setFormErrors((prev) => ({ ...prev, phone: "" }));
                      }
                    }}
                    placeholder="+34 123 456 789"
                  />
                  {formErrors.phone && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.phone}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.status}
                  </label>
                  <select
                    disabled={mode === "show"}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.status ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value as "active" | "inactive",
                      })
                    }
                  >
                    <option value="active">{translations.activeFemale}</option>
                    <option value="inactive">
                      {translations.inactiveFemale}
                    </option>
                  </select>
                  {formErrors.status && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.status}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.address}
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent  ${
                    formErrors.address ? "border-red-500" : "border-gray-300"
                  }`}
                  value={formData.address}
                  onChange={(e) => {
                    setFormData({ ...formData, address: e.target.value });
                    if (formErrors.address) {
                      setFormErrors((prev) => ({
                        ...prev,
                        address: "",
                      }));
                    }
                  }}
                  placeholder={translations.addressPlaceholder}
                />
                {formErrors.address && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.address}
                  </p>
                )}
              </div>
            </>
          ) : (
            <></>
          )}
          {type === "user" && isFormData(formData, "user") ? (
            <>
              {/* User Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.fullName} *
                  </label>
                  <input
                    disabled={mode === "show"}
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.fullName ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.fullName}
                    onChange={(e) => {
                      setFormData({ ...formData, fullName: e.target.value });
                      if (formErrors.fullName) {
                        setFormErrors((prev) => ({ ...prev, fullName: "" }));
                      }
                    }}
                    placeholder={translations.fullNamePlaceholder}
                  />
                  {formErrors.fullName && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.fullName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.userEmail} *
                  </label>
                  <input
                    type="email"
                    disabled={mode === "show"}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.email ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (formErrors.email) {
                        setFormErrors((prev) => ({ ...prev, email: "" }));
                      }
                    }}
                    placeholder="usuario@email.com"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.role} *
                  </label>
                  {roles.length > 0 && (
                    <select
                      disabled={mode === "show"}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.roleId}
                      onChange={(e) => {
                        const selectedRoleId = e.target.value;
                        const selectedRole =
                          roles.find((role: any) => role.id === selectedRoleId)
                            ?.name || "Organization";
                        setFormData({
                          ...formData,
                          roleId: selectedRoleId,
                          role: selectedRole,
                          organizationId:
                            selectedRole === "Administrator"
                              ? ""
                              : formData.organizationId,
                        });
                      }}
                    >
                      <option value="" disabled>
                        {translations.selectRole}
                      </option>
                      {roles.map((role: any) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {translations.onlyAdminsCanCreate}
                  </p>
                </div>
                {formData.role === "Organization" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {translations.organization}{" "}
                      {formData.role === "Organization" ? "*" : ""}
                    </label>
                    {entities.length === 0 && !loadingEntities && (
                      <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                        {userRole === UserRole.Administrator ? (
                          <>
                            <p className="text-gray-700 mb-2">
                              {translations.noEntitiesAvailable}
                            </p>
                            <button
                              type="button"
                              className="text-blue-600 hover:text-blue-800 font-medium text-sm hover:underline focus:outline-none"
                              onClick={() => {
                                setNotification({
                                  type: "info",
                                  title: translations.createNewEntity,
                                  message: translations.redirectingEntities,
                                });
                                if (onNavigateToEntities) {
                                  onNavigateToEntities();
                                }
                              }}
                            >
                              {translations.createNewEntity}
                            </button>
                          </>
                        ) : (
                          <p className="text-gray-700">
                            {translations.noEntitiesOrganization}
                          </p>
                        )}
                      </div>
                    )}
                    {entities.length > 0 && (
                      <select
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formErrors.organizationId
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        value={formData.organizationId}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            organizationId: e.target.value,
                          });
                          if (formErrors.organizationId) {
                            setFormErrors((prev) => ({ ...prev, organizationId: "" }));
                          }
                        }}
                      >
                        {mode === "edit" && (
                          <option value="">
                            {formData.role === "Organization" && (
                              <>{translations.selectOrganization}</>
                            )}
                          </option>
                        )}
                        {entities.map((entity) => (
                          <option key={entity.id} value={entity.id}>
                            {entity.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {formErrors.organizationId && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.organizationId}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {mode === "edit" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {translations.password}
                      {!isEditing && " *"}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formErrors.password
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        value={formData.password}
                        onChange={(e) => {
                        const newPassword = e.target.value;
                        setFormData({
                          ...formData,
                          password: newPassword,
                        });
                        
                        // Limpiar errores previos
                        setFormErrors((prev) => ({
                          ...prev,
                          password: "",
                        }));
                        
                        // Si hay confirmación de contraseña, validar que coincidan
                        if (formData.confirmPassword) {
                          if (newPassword !== formData.confirmPassword) {
                            setFormErrors((prev) => ({
                              ...prev,
                              confirmPassword: translations.passwordsDoNotMatch,
                            }));
                          } else {
                            setFormErrors((prev) => ({
                              ...prev,
                              confirmPassword: "",
                            }));
                          }
                        }
                        
                        // Validar requisitos de contraseña solo si hay texto o es creación
                        if (!isEditing || newPassword) {
                          const meetsLength = newPassword.length >= 8;
                          const hasUpper = /[A-Z]/.test(newPassword);
                          const hasLower = /[a-z]/.test(newPassword);
                          const hasNumber = /\d/.test(newPassword);
                          const anyMissing = !meetsLength || !hasUpper || !hasLower || !hasNumber;
                          
                          if (newPassword && anyMissing) {
                            setFormErrors((prev) => ({
                              ...prev,
                              password: translations.passwordRequirements as string,
                            }));
                          }
                        }
                        }}
                        placeholder={translations.minLength}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {formErrors.password && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.password}
                      </p>
                    )}
                    <div className="mt-2 space-y-1 text-xs">
                      {(() => {
                        const pwd = formData.password || "";
                        const items = [
                          { ok: pwd.length >= 8, text: translations.minEightChars || "Mínimo 8 caracteres" },
                          { ok: /[A-Z]/.test(pwd), text: translations.oneUppercase || "Una mayúscula" },
                          { ok: /[a-z]/.test(pwd), text: translations.oneLowercase || "Una minúscula" },
                          { ok: /\d/.test(pwd), text: translations.oneNumber || "Un número" },
                        ];
                        return items.map((it, idx) => (
                          <div key={idx} className={`flex items-center ${it.ok ? "text-green-600" : "text-gray-500"}`}>
                            <span className={`w-2 h-2 rounded-full mr-2 ${it.ok ? "bg-green-600" : "bg-gray-400"}`}></span>
                            <span>{it.text}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {translations.confirmPassword}
                      {!isEditing && " *"}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formErrors.confirmPassword
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        value={formData.confirmPassword}
                        onChange={(e) => {
                        const newConfirmPassword = e.target.value;
                        setFormData({
                          ...formData,
                          confirmPassword: newConfirmPassword,
                        });

                        // Limpiar error previo
                        setFormErrors((prev) => ({
                          ...prev,
                          confirmPassword: "",
                        }));

                        if (formData.password !== newConfirmPassword) {
                          setFormErrors((prev) => ({
                            ...prev,
                            confirmPassword: translations.passwordsDoNotMatch,
                          }));
                        }
                        }}
                        placeholder={translations.repeatPassword}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {formErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.status}
                </label>
                <select
                  disabled={mode === "show"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as "active" | "inactive",
                    })
                  }
                >
                  <option value="active">{translations.active}</option>
                  <option value="inactive">{translations.inactive}</option>
                </select>
              </div>
            </>
          ) : (
            <></>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {mode === "edit" ? translations.cancel : translations.close}
            </button>
            {mode === "edit" && (
              <button
                type="submit"
                disabled={!isFormValid() || isSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                {isEditing ? translations.update : translations.create}{" "}
                {type === "entity" ? translations.entity : translations.user}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Info Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <div className="w-5 h-5 text-blue-400">ℹ️</div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              {type === "entity"
                ? translations.managementEntities
                : translations.managementUsers}
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                {type === "entity" ? (
                  <>
                    <li>{translations.entitiesDescription}</li>
                    <li>{translations.usersDescription}</li>
                    <li>{translations.activeEntitiesDescription}</li>
                    <li>{translations.contactInfoDescription}</li>
                  </>
                ) : (
                  <>
                    <li>{translations.onlyAdminsCanCreate}</li>
                    <li>{translations.organizationUsersMustBeAssigned}</li>
                    <li>{translations.adminsHaveFullAccess}</li>
                    <li>{translations.inactiveUsersNoAccess}</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
